// Core
import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useRunOperationTaskMutation } from "state/communities/communitiesApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { handleStartTask } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

// Components
import { Card, Icon, IconButton, Tooltip, Typography } from "@mui/material";
import Box from "components/Atoms/Box";
import BoxV1 from "components/Atoms/Box/BoxV1";
import TypographyV1 from "components/Atoms/Typography/TypographyV1";

function RunOperations() {
  const { t } = useTranslation(["runOperations", "common"]);
  const community = useSelector(selectCurrentCommunity);
  // eslint-disable-next-line no-unused-vars
  const [_, setLastTaskClicked] = useState(null);
  const dispatch = useDispatch();

  const SETTLEMENT_TASK_TYPE = "PROD_RUN_SETTLEMENT";
  const CLEAR_MARKET_TASK_TYPE = "PROD_CLEAR_MARKET";

  const activeSettlementTasks = useSelector(selectActiveTasksByType(SETTLEMENT_TASK_TYPE));
  const activeClearMarketTasks = useSelector(selectActiveTasksByType(CLEAR_MARKET_TASK_TYPE));

  const returnActiveTasks = () => {
    if (activeSettlementTasks.length > 0) {
      return activeSettlementTasks;
    }
    if (activeClearMarketTasks.length > 0) {
      return activeClearMarketTasks;
    }
    return [];
  };

  // GET Task Status
  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    {
      taskId: returnActiveTasks()[0]?.id,
      communityId: community.id,
      groupTask: returnActiveTasks()[0]?.groupTask ? "true" : "false",
    },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: returnActiveTasks().length === 0,
    }
  );

  // Clear Market
  const [runClearMarket] = useRunOperationTaskMutation();
  const lastClearMarketTime = localStorage.getItem("lastProdClearMarketTime");
  const handleRunClearMarket = () => {
    localStorage.setItem("lastProdClearMarketTime", Date.now().toString());
    handleStartTask(
      runClearMarket,
      { communityId: community.id, taskName: "clear_market" },
      dispatch,
      CLEAR_MARKET_TASK_TYPE
    )();
    setLastTaskClicked(CLEAR_MARKET_TASK_TYPE);
  };

  // Run Settlement
  const [runSettlement] = useRunOperationTaskMutation();
  const lastSettlementTime = localStorage.getItem("lastProdSettlementTime");
  const handleRunSettlement = () => {
    localStorage.setItem("lastProdSettlementTime", Date.now().toString());
    handleStartTask(
      runSettlement,
      { communityId: community.id, taskName: "run_settlement" },
      dispatch,
      SETTLEMENT_TASK_TYPE,
      false
    )();
    setLastTaskClicked(SETTLEMENT_TASK_TYPE);
  };

  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: returnActiveTasks(),
    dispatch,
    type: returnActiveTasks()[0]?.type,
  });

  const isLoading = returnActiveTasks().length > 0;

  const operations = [
    {
      id: "clearmarket",
      icon: "storefront",
      color: "#82d616",
      title: t("clearmarket.button"),
      description: t("clearmarket.helptext"),
      onClick: handleRunClearMarket,
      timestamp: lastClearMarketTime,
    },
    {
      id: "settlement",
      icon: "receipt_long",
      color: "#cb0c9f",
      title: t("settlement.button"),
      description: t("settlement.helptext"),
      onClick: handleRunSettlement,
      timestamp: lastSettlementTime,
    },
  ];

  return (
    <Card id="run-operations-control" className="overflow-visible">
      <BoxV1 p={3}>
        <TypographyV1 variant="h5">{t("headerTitle")}</TypographyV1>
        <TypographyV1 variant="body2" color="text" sx={{ mt: 0.5 }}>
          {t("headerDescription")}
        </TypographyV1>
      </BoxV1>
      <Box>
        {operations.map((operation) => (
          <Box key={operation.id}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: 3,
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Icon */}
                <Icon sx={{ color: operation.color, fontSize: "1.5rem", flexShrink: 0 }}>
                  {operation.icon}
                </Icon>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      color: (theme) => (theme.palette.mode === "dark" ? "#e0e6ed" : "#344767"),
                      mb: 0.5,
                    }}
                  >
                    {operation.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                  >
                    {operation.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      mt: 0.5,
                      display: "block",
                    }}
                  >
                    {`${t("common:lastran")}: ${
                      operation.timestamp
                        ? moment(parseInt(operation.timestamp, 10)).fromNow()
                        : "--"
                    }`}
                  </Typography>
                </Box>

                {/* Action Button */}
                <Box sx={{ flexShrink: 0 }}>
                  <Tooltip title={t("common:run")}>
                    <IconButton
                      onClick={operation.onClick}
                      disabled={isLoading}
                      sx={{
                        backgroundColor: "#17c1e8",
                        color: "white",
                        width: 36,
                        height: 36,
                        "&:hover": {
                          backgroundColor: "#1ab0d4",
                        },
                        "&:disabled": {
                          backgroundColor: "rgba(0, 0, 0, 0.12)",
                          color: "rgba(0, 0, 0, 0.26)",
                        },
                      }}
                    >
                      <Icon sx={{ fontSize: "1.25rem" }}>play_arrow</Icon>
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

export default RunOperations;
